import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { ENV_CONFIG } from 'src/bin/env.config';
import { SalonmonsterHttpClient } from './salonmonster-http-client';
import { UserService } from './user.service';
import { ErrorHandlerService } from './error-handler.service';
import { Utilities } from '../utilities/utilities';

export type ExportType =
  | 'staff'
  | 'services'
  | 'services-detailed'
  | 'clients'
  | 'bookings'
  | 'products'
  | 'sales';

export interface ExportRequest {
  salonID: number;
  types: ExportType[] | 'all';
}

export interface ExportResponse {
  success: boolean;
  totalExports: number;
  successfulExports: number;
  failedExports: number;
  files: Array<{
    filename: string;
    success: boolean;
    error?: string;
  }>;
  errors?: string[];
}

export interface EmailExportRequest extends ExportRequest {
  recipientEmail: string;
  recipientName?: string;
}

export interface EmailExportResponse {
  success: boolean;
  message?: string;
  email?: {
    recipient: string;
    externalMessageID?: string;
    attachmentSizeBytes?: number;
  };
  err_msg?: string;
  error?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ExportService extends SalonmonsterHttpClient {
  constructor(
    protected override http: HttpClient,
    protected override userService: UserService,
    protected override errorHandlerService: ErrorHandlerService
  ) {
    super(http, userService, errorHandlerService);
  }

  /**
   * Request a ZIP file that contains CSV exports for the selected datasets.
   */
  public exportData(request: ExportRequest): Observable<Blob> {
    if (Utilities.isCapacitorNative()) {
      return this.exportViaHttpClient(request);
    }

    return from(this.exportViaFetch(request));
  }

  /**
   * Light-weight endpoint for validating connectivity. Optional.
   */
  public testConnection(salonID: number): Observable<any> {
    const url = `${ENV_CONFIG.API_ROOT}/salons/${salonID}/exports/test`;
    return this.get(url);
  }

  /**
   * Request the backend to email the export to the authenticated user.
   */
  public emailExport(request: EmailExportRequest): Observable<EmailExportResponse> {
    const url = `${ENV_CONFIG.API_ROOT}/salons/${request.salonID}/exports/email`;
    const headers = this.createAuthorizationHeader(new HttpHeaders());

    const body = {
      salonID: request.salonID,
      types: this.buildEmailTypesPayload(request.types),
      recipientEmail: request.recipientEmail,
      recipientName: request.recipientName,
    };

    return this.http
      .post<EmailExportResponse>(url, body, {
        headers,
      })
      .pipe(
        catchError((err) =>
          throwError(() => this.errorHandlerService.handleError(err))
        )
      );
  }

  private exportViaHttpClient(request: ExportRequest): Observable<Blob> {
    const url = `${ENV_CONFIG.API_ROOT}/salons/${request.salonID}/exports`;
    const params = new HttpParams().set('types', this.buildTypesParam(request));
    const headers = this.createAuthorizationHeader(new HttpHeaders());

    return this.http
      .get(url, {
        headers,
        params,
        responseType: 'blob',
      })
      .pipe(catchError((err) => this.normalizeBlobError(err)));
  }

  private async exportViaFetch(request: ExportRequest): Promise<Blob> {
    const url = new URL(`${ENV_CONFIG.API_ROOT}/salons/${request.salonID}/exports`);
    url.searchParams.set('types', this.buildTypesParam(request));

    const headers = this.headersToObject(this.createAuthorizationHeader(new HttpHeaders()));

    let response: Response;

    try {
      response = await fetch(url.toString(), {
        method: 'GET',
        headers,
      });
    } catch (err) {
      throw this.errorHandlerService.handleError(err);
    }

    if (!response.ok) {
      const text = await response.text();
      let parsedError: any = text;
      try {
        parsedError = text ? JSON.parse(text) : {};
      } catch (_) {
        parsedError = { message: text };
      }

      throw this.errorHandlerService.handleError(
        new HttpErrorResponse({
          status: response.status,
          statusText: response.statusText,
          url: response.url,
          error: parsedError,
        })
      );
    }

    return response.blob();
  }

  private buildTypesParam(request: ExportRequest): string {
    if (request.types === 'all') {
      return 'all';
    }

    if (!request.types || request.types.length === 0) {
      return 'all';
    }

    const uniqueTypes = Array.from(new Set(request.types));
    return uniqueTypes.join(',');
  }

  private headersToObject(headers: HttpHeaders): Record<string, string> {
    return headers.keys().reduce((acc, key) => {
      const value = headers.get(key);
      if (value !== null) {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, string>);
  }

  private buildEmailTypesPayload(types: ExportType[] | 'all'): ExportType[] | 'all' {
    if (types === 'all') {
      return 'all';
    }

    if (!types || types.length === 0) {
      return 'all';
    }

    return Array.from(new Set(types));
  }

  private normalizeBlobError(err: HttpErrorResponse) {
    if (err?.error instanceof Blob) {
      return from(err.error.text()).pipe(
        switchMap((text) => {
          let parsedError: any = err.error;
          try {
            parsedError = text ? JSON.parse(text) : {};
          } catch (_) {
            parsedError = { message: text };
          }

          const normalizedError = this.errorHandlerService.handleError(
            new HttpErrorResponse({
              ...err,
              error: parsedError,
              status: err.status,
              statusText: err.statusText,
            })
          );

          return throwError(() => normalizedError);
        })
      );
    }

    const normalizedError = this.errorHandlerService.handleError(err);
    return throwError(() => normalizedError);
  }
}

